import { Offcanvas, Form, Card, Row, Col } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { XCircleFill } from "react-bootstrap-icons";
import { useRef } from "react";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  name: string;
  location: string;
  date: string;
  endDate: string;
  url: string;
  color: string;
};

const AddEventCalendar = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue, // ✅ Needed for prefill
    watch,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [removeImage, setRemoveImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);  

  // ✅ Tag dropdown states

  // ✅ Load data on open
  useEffect(() => {
    if (show) {
      reset({
        name: data?.name || "",
        location: data?.location || "",

        date: data?.date ? new Date(data.date).toISOString().slice(0, 10) : "",
        endDate: data?.endDate
          ? new Date(data.endDate).toISOString().slice(0, 10)
          : "",
        url: data?.url || "",
        color: data?.color || "#000000",
      });
      if (data?.image) {
        setFile(null); // existing handled separately
        setRemoveImage(false);
      }
    } else {
      reset({});
      setFile(null);
      setRemoveImage(false);
    }
  }, [show, data]);
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  // ✅ Submit handler
  const onSubmit = (request: FormData) => {
    if (fileError) {
      toast.error(fileError);
      return;
    }
    // Image required validation
    if (!file && (!data?.image || removeImage)) {
      toast.error("Image is required");
      return;
    }    
    setLoading(true);
    const formData = new FormData();
    formData.append("name", request.name);
    formData.append("location", request.location || "");
    formData.append("color", request.color);
    formData.append("url", request.url ? request.url.trim() : "");
    if (file) {
      formData.append("image", file);
    }
    if (removeImage) {
      formData.append("removeImage", "true");
    }
    if (request.date) {
      const selectedDate = new Date(request.date);
      console.log("Request Data:", selectedDate);

      // Convert local datetime to ISO (for consistent DB storage)
      const now = new Date();
      selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

      const dateISO = selectedDate.toISOString();
      console.log("Request Data:", dateISO);
      formData.append("date", dateISO);
    }
    if (request.endDate) {
      const selectedEndDate = new Date(request.endDate);
      // Convert local datetime to ISO (for consistent DB storage)
      const now = new Date();
      selectedEndDate.setHours(
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      const endDateISO = selectedEndDate.toISOString();
      console.log("Request Data:", endDateISO);
      formData.append("endDate", endDateISO);
    } else{
      formData.append("endDate", "");
    }
    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
          action: `event-calendar/edit/${data._id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `event-calendar/add`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setFile(null);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setFile(null);
        handleClose();
        setRemoveImage(false);
        if (imageInputRef.current) imageInputRef.current.value = "";
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {data ? "Edit Event Calendar" : "Add Event Calendar"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>
            {/* Name */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <div className="error-message">{errors.name.message}</div>
              )}
            </Form.Group>

            {/* Location */}
            <Form.Group className="mb-2" controlId="eventLocation">
              <Form.Label className="label">
                Location <span className="text-danger">*</span>
                {/* <span className="text-danger">*</span> */}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter location"
                {...register("location", { required: "Location is required" })}
              />
              {errors.location && (
                <div className="error-message">{errors.location.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                Event Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                {...register("date", { required: "Date is required" })}
                value={watch("date") || ""}
                onChange={(e) => setValue("date", e.target.value)}
              />
              {errors.date && (
                <div className="error-message">{errors.date.message}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventEndDate">
              <Form.Label className="label">End Date</Form.Label>
              <Form.Control
                type="date"
                {...register("endDate")}
                value={watch("endDate") || ""}
                onChange={(e) => setValue("endDate", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="eventUrl">
              <Form.Label className="label">URL</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com"
                {...register("url", {
                  pattern: {
                    value: /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i,
                    message: "Please enter a valid URL (starting with http:// or https://)",
                  },
                })}
              />
              {errors.url && (
                <div className="error-message">{errors.url.message}</div>
              )}
            </Form.Group>
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <Row>
              <Col>
                {/* Image */}
                <Form.Group className="mb-2" controlId="eventImage">
                  <Form.Label className="label">
                    Image <span className="text-danger">*</span>
                  </Form.Label>
                  <p
                    className="mt-1 text-primary"
                    style={{ fontSize: "13px" }}
                  >
                    Note: Please upload an image of size:{" "}
                    <strong>95 × 49 px</strong> in{" "}
                    <strong>.png, .jpeg, .jpg or .svg</strong> format.
                  </p>
                  <Form.Control
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    className="custom-image-upload"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const selectedFile = input.files?.[0];

                      if (!selectedFile) return;

                      if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
                        setFileError(
                          "Image format not supported. Please upload PNG, JPG, JPEG or SVG. image"
                        );
                        // console.log(fileError)
                        //      toast.error(fileError);
                        setFile(null);
                        input.value = ""; // reset input
                        return;
                      }

                      // ✅ valid
                      setFileError("");
                      setFile(selectedFile);
                      setRemoveImage(false); // important
                    }}
                  />
                  {fileError && (
                    <p
                      className="text-danger mt-1"
                      style={{ fontSize: "13px" }}
                    >
                      {fileError}
                    </p>
                  )}
                </Form.Group>
                {/* New Image Preview */}
                {file && (
                  <div className="mb-2 event-image position-relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Event"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />

                    <XCircleFill
                      size={22}
                      color="red"
                      className="position-absolute cursor-pointer"
                      style={{ top: 8, right: 8 }}
                      onClick={() => {
                        setFile(null);
                        if (imageInputRef.current) {
                          imageInputRef.current.value = "";
                        }
                      }}
                    />
                  </div>
                )}

                {/* Existing Image Preview (Edit mode) */}
                {!file && data?.image && !removeImage && (
                  <div className="mb-2 event-image position-relative">
                    <img
                      src={data.image}
                      alt="Event"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />

                    <XCircleFill
                      size={22}
                      color="red"
                      className="position-absolute cursor-pointer"
                      style={{ top: 8, right: 8 }}
                      onClick={() => setRemoveImage(true)}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <Card>
            {/* Color Picker */}
            <Form.Group className="mb-2" controlId="eventColor">
              <Form.Label className="label">
                Color <span className="text-danger">*</span>
              </Form.Label>

              <Form.Control
                type="color"
                {...register("color", { required: "Color is required" })}
                value={watch("color") || "#000000"}
                onChange={(e) => setValue("color", e.target.value)}
                style={{ width: "60px", height: "40px", padding: "2px" }}
              />

              {errors.color && (
                <div className="error-message">{errors.color.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddEventCalendar;
